import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ExamAnswersOptions } from "src/components/Exam/Answers/ExamAnswersOptions";
import Question from "src/components/Exam/QuestionBox/Question";
import ExamInfo from "src/components/NewComponents/Exam/ExamInfo";
import { ExamOptions } from "src/components/NewComponents/Exam/Option/ExamOptions";
import { isFinishedExam, isMultiAnswersExam } from "src/helpers/examHelpers";
import { useGetExamQuestionDetails } from "src/hooks/useExam";
import { useQuestionSettings } from "src/reducers/exam";

import { Swipeable } from "../../Default/Swipeable";
import QuestionController from "./QuestionController";

interface SelectedQuestion {
	id: string | number;
	pytanie: string;
	answer: string | null;
	correctAnswer: string;
	isCanceled: boolean;
	answers: Record<string, string>;
	images?: Array<{ url: string; [key: string]: unknown }>;
	excludedAnswers: Array<{ answer: string }>;
	termin: string;
	dzial?: string;
	isInBazaPytan?: boolean;
	pytanie_id_in_cem_pdf?: string | number;
}

interface SetAnswerPayload {
	questionId: string | number;
	answer: string;
	isCorrectAnswer: boolean;
	isAnnulledQuestion: boolean;
}

interface QuestionBoxProps {
	selectedQuestion: SelectedQuestion;
	setAnswer: (payload: SetAnswerPayload) => Promise<void>;
	examType: string;
	examStatus: string;
	isAnswerSet: boolean;
	isAnnulledQuestion: boolean;
	isLastQuestion: boolean;
	isFirstQuestion: boolean;
	onNext: () => void;
	onPrev: () => void;
	toggleIsMenuOpen: () => void;
	viewOnly?: boolean;
	userAnswer: string | null;
}

function QuestionBox(props: QuestionBoxProps) {
	const { t } = useTranslation();
	const params = useParams<{ id: string }>();
	const examId = params.id;
	const { state } = useQuestionSettings();
	const { data: details = {} } = useGetExamQuestionDetails(
		props.selectedQuestion.id,
		examId,
	);
	const refContainer = useRef<HTMLDivElement>(null);

	const scrollToTop = () => {
		if (!refContainer.current) {
			return null;
		}

		refContainer.current.scrollTop = 0;
	};

	const renderCancelledQuestionInfo = () => {
		const isMultiAnswerExam = isMultiAnswersExam(props.examType);
		const shouldShowWarning =
			(isMultiAnswerExam &&
				isFinishedExam(props.examStatus) &&
				props.isAnnulledQuestion) ||
			(!isMultiAnswerExam && props.isAnnulledQuestion && props.isAnswerSet);

		if (shouldShowWarning) {
			return (
				<div className="answer--warning p-8 list-item-answer">
					{t("exam.correctAnswerNotExistText")}
				</div>
			);
		}

		return null;
	};

	const renderNotAnswerSetInfo = () => {
		if (isFinishedExam(props.examStatus) && !props.isAnswerSet) {
			return (
				<div className="answer--warning p-8 list-item-answer">
					{t("exam.answerNotSetText")}
				</div>
			);
		}

		return null;
	};

	const onNext = () => {
		scrollToTop();
		props.onNext();
	};

	const onPrev = () => {
		scrollToTop();
		props.onPrev();
	};

	return (
		<Swipeable
			onSwipedRight={onPrev}
			onSwipedLeft={onNext}
			className="exam-question-box"
		>
			<div className="exam-question-box--container" ref={refContainer}>
				<ExamInfo toggleIsMenuOpen={props.toggleIsMenuOpen} />
				<div className="question-container">
					<div
						className="question-container--question"
						id="question-container"
						data-test-id="exam-question"
						data-correct-answer={props.selectedQuestion.correctAnswer}
					>
						<Question
							questionDetails={details}
							question={props.selectedQuestion}
							isAnswerSet={props.isAnswerSet}
							state={state}
						/>
						{renderNotAnswerSetInfo()}
						{renderCancelledQuestionInfo()}
						<ExamAnswersOptions
							question={props.selectedQuestion}
							setAnswer={props.setAnswer}
						/>
						<ExamOptions />
					</div>
				</div>
				<QuestionController
					onNext={onNext}
					onPrev={onPrev}
					isLastQuestion={props.isLastQuestion}
					isFirstQuestion={props.isFirstQuestion}
				/>
			</div>
		</Swipeable>
	);
}

export default QuestionBox;
